import { useCallback, useRef } from "react";
import type { NostrEvent, NostrSigner } from "@nostrify/nostrify";
import { useNostr } from "@nostrify/react";
import { useCurrentUser } from "./useCurrentUser";
import { TOOL_DEFS, runTool, type ToolResult } from "@/lib/nostrTools";

const SHAKESPEARE_API_URL = "https://ai.shakespeare.diy/v1";
const MAX_TOOL_ROUNDS = 6;

// --- OpenAI-compatible message shapes ---
interface ToolCall {
  id: string;
  type: "function";
  function: { name: string; arguments: string };
}

interface ApiMessage {
  role: "system" | "user" | "assistant" | "tool";
  content: string | null;
  tool_calls?: ToolCall[];
  tool_call_id?: string;
  name?: string;
}

export interface DraftEvent {
  kind: number;
  content: string;
  tags: string[][];
}

/** Callbacks the UI provides to observe the agent's progress. */
export interface AgentCallbacks {
  onTextChunk: (chunk: string) => void;
  onToolStart: (name: string, args: Record<string, unknown>) => void;
  onEvents: (events: NostrEvent[]) => void;
  onDraft: (draft: DraftEvent) => void;
}

async function createNIP98Token(
  method: string,
  url: string,
  body: unknown,
  signer: NostrSigner
): Promise<string> {
  const tags: string[][] = [
    ["u", url],
    ["method", method],
  ];
  if (body) {
    const data = new TextEncoder().encode(JSON.stringify(body));
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const payloadHash = Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    tags.push(["payload", payloadHash]);
  }
  const event = await signer.signEvent({
    kind: 27235,
    content: "",
    tags,
    created_at: Math.floor(Date.now() / 1000),
  });
  return btoa(JSON.stringify(event));
}

interface StreamResult {
  text: string;
  toolCalls: ToolCall[];
}

export function useKindlessAgent() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const abortRef = useRef<AbortController | null>(null);

  const streamCompletion = useCallback(
    async (
      messages: ApiMessage[],
      model: string,
      signal: AbortSignal,
      onTextChunk: (c: string) => void,
      useTools: boolean
    ): Promise<StreamResult> => {
      if (!user?.signer) throw new Error("You must be logged in to use the AI.");

      const body: Record<string, unknown> = {
        model,
        messages,
        stream: true,
        temperature: 0.4,
      };
      if (useTools) {
        body.tools = TOOL_DEFS;
        body.tool_choice = "auto";
      }

      const token = await createNIP98Token(
        "POST",
        `${SHAKESPEARE_API_URL}/chat/completions`,
        body,
        user.signer
      );

      const response = await fetch(`${SHAKESPEARE_API_URL}/chat/completions`, {
        method: "POST",
        headers: {
          Authorization: `Nostr ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error("Authentication failed. Please log in again.");
        if (response.status === 402)
          throw new Error("Insufficient credits. Add credits or try a free model.");
        // Read the raw body so we can surface (and react to) the real cause.
        let detail = response.statusText;
        const raw = await response.text().catch(() => "");
        if (raw) {
          try {
            const j = JSON.parse(raw);
            detail = j.error?.message ?? j.message ?? j.error ?? raw;
          } catch {
            detail = raw;
          }
        }
        const err = new Error(`AI error (${response.status}): ${detail}`) as Error & {
          status?: number;
          usedTools?: boolean;
        };
        err.status = response.status;
        err.usedTools = useTools;
        throw err;
      }
      if (!response.body) throw new Error("No response body from AI.");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let text = "";
      // Accumulate tool calls by index.
      const toolAcc = new Map<number, { id: string; name: string; args: string }>();
      let buffer = "";

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith("data:")) continue;
            const data = trimmed.slice(5).trim();
            if (data === "[DONE]") continue;
            let parsed: {
              choices?: Array<{
                delta?: {
                  content?: string;
                  tool_calls?: Array<{
                    index?: number;
                    id?: string;
                    function?: { name?: string; arguments?: string };
                  }>;
                };
              }>;
            };
            try {
              parsed = JSON.parse(data);
            } catch {
              continue;
            }
            const delta = parsed.choices?.[0]?.delta;
            if (!delta) continue;
            if (delta.content) {
              text += delta.content;
              onTextChunk(delta.content);
            }
            if (delta.tool_calls) {
              for (const tc of delta.tool_calls) {
                const idx = typeof tc.index === "number" ? tc.index : 0;
                const cur = toolAcc.get(idx) ?? { id: "", name: "", args: "" };
                if (tc.id) cur.id = tc.id;
                if (tc.function?.name) cur.name = tc.function.name;
                if (tc.function?.arguments) cur.args += tc.function.arguments;
                toolAcc.set(idx, cur);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      const toolCalls: ToolCall[] = [...toolAcc.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([, v], i) => ({
          id: v.id || `call_${i}`,
          type: "function" as const,
          function: { name: v.name, arguments: v.args || "{}" },
        }))
        .filter((t) => t.function.name);

      return { text, toolCalls };
    },
    [user]
  );

  const run = useCallback(
    async (
      history: { role: "user" | "assistant"; content: string }[],
      systemPrompt: string,
      model: string,
      cb: AgentCallbacks
    ): Promise<string> => {
      const controller = new AbortController();
      abortRef.current = controller;
      const signal = controller.signal;

      const baseMessages: ApiMessage[] = [
        { role: "system", content: systemPrompt },
        ...history.map((m) => ({ role: m.role, content: m.content })),
      ];

      // Run the agentic loop. `useTools` can be turned off to fall back to a
      // plain completion if the model/gateway rejects function calling.
      const loop = async (useTools: boolean): Promise<string> => {
        const messages = baseMessages.map((m) => ({ ...m }));
        let finalText = "";

        for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
          const { text, toolCalls } = await streamCompletion(
            messages,
            model,
            signal,
            (c) => cb.onTextChunk(c),
            useTools
          );
          finalText += text;

          if (!useTools || !toolCalls.length) {
            break;
          }

          // Record the assistant's tool-call turn.
          messages.push({ role: "assistant", content: text || null, tool_calls: toolCalls });

          // Execute each tool and feed results back.
          for (const call of toolCalls) {
            let parsedArgs: Record<string, unknown> = {};
            try {
              parsedArgs = JSON.parse(call.function.arguments || "{}");
            } catch {
              parsedArgs = {};
            }
            cb.onToolStart(call.function.name, parsedArgs);

            let result: ToolResult;
            try {
              result = await runTool(call.function.name, parsedArgs, {
                nostr,
                userPubkey: user?.pubkey,
                signal,
              });
            } catch (e) {
              result = { content: `Tool error: ${(e as Error).message}` };
            }

            if (result.events?.length) cb.onEvents(result.events);
            if (result.draft) cb.onDraft(result.draft);

            messages.push({
              role: "tool",
              tool_call_id: call.id,
              name: call.function.name,
              content: result.content,
            });
          }
          // Add a separating newline before the model continues.
          cb.onTextChunk("\n\n");
          finalText += "\n\n";
        }
        return finalText;
      };

      try {
        const result = await loop(true);
        abortRef.current = null;
        return result;
      } catch (e) {
        abortRef.current = null;
        // Surface the real error. We deliberately do NOT silently retry without
        // tools — that would hide the actual cause behind a misleading message.
        throw e;
      }
    },
    [streamCompletion, nostr, user]
  );

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  return { run, abort, isAuthenticated: !!user };
}
